/**
 * Location Model for V2 Services
 */

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  formatted?: string;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface BuildingData {
  id?: string;
  name: string;
  address: Address;
  coordinates?: Coordinates;
  floors: number;
  totalApartments: number;
  amenities?: string[];
  managedBy?: string;
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface ApartmentData {
  id?: string;
  buildingId: string;
  floor: number;
  apartmentNumber: string;
  userId?: string;
  squareFeet?: number;
  bedrooms?: number;
  bathrooms?: number;
  lastCleaning?: Date;
  nextCleaning?: Date;
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export class Building {
  public id?: string;
  public name: string;
  public address: Address;
  public coordinates?: Coordinates;
  public floors: number;
  public totalApartments: number;
  public amenities: string[];
  public managedBy?: string;
  public metadata: Record<string, any>;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(data: Partial<BuildingData>) {
    this.id = data.id;
    this.name = data.name || '';
    this.address = data.address || {};
    this.coordinates = data.coordinates;
    this.floors = data.floors || 1;
    this.totalApartments = data.totalApartments || 0;
    this.amenities = data.amenities || [];
    this.managedBy = data.managedBy;
    this.metadata = data.metadata || {};
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  public toFirestore(): Record<string, any> {
    return {
      name: this.name,
      address: this.address,
      coordinates: this.coordinates,
      floors: this.floors,
      totalApartments: this.totalApartments,
      amenities: this.amenities,
      managedBy: this.managedBy,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  public static fromFirestore(data: any): Building {
    return new Building({
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt
    });
  }

  public updateInfo(updates: Partial<BuildingData>): void {
    Object.assign(this, updates);
    this.updatedAt = new Date();
  }
}

export class Apartment {
  public id?: string;
  public buildingId: string;
  public floor: number;
  public apartmentNumber: string;
  public userId?: string;
  public squareFeet?: number;
  public bedrooms?: number;
  public bathrooms?: number;
  public lastCleaning?: Date;
  public nextCleaning?: Date;
  public metadata: Record<string, any>;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(data: Partial<ApartmentData>) {
    this.id = data.id;
    this.buildingId = data.buildingId || '';
    this.floor = data.floor || 1;
    this.apartmentNumber = data.apartmentNumber || '';
    this.userId = data.userId;
    this.squareFeet = data.squareFeet;
    this.bedrooms = data.bedrooms;
    this.bathrooms = data.bathrooms;
    this.lastCleaning = data.lastCleaning;
    this.nextCleaning = data.nextCleaning;
    this.metadata = data.metadata || {};
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  public toFirestore(): Record<string, any> {
    return {
      buildingId: this.buildingId,
      floor: this.floor,
      apartmentNumber: this.apartmentNumber,
      userId: this.userId,
      squareFeet: this.squareFeet,
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      lastCleaning: this.lastCleaning,
      nextCleaning: this.nextCleaning,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  public static fromFirestore(data: any): Apartment {
    return new Apartment({
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      lastCleaning: data.lastCleaning?.toDate?.() || data.lastCleaning,
      nextCleaning: data.nextCleaning?.toDate?.() || data.nextCleaning
    });
  }

  public assignToUser(userId: string): void {
    this.userId = userId;
    this.updatedAt = new Date();
  }

  public updateCleaningSchedule(lastCleaning: Date, nextCleaning: Date): void {
    this.lastCleaning = lastCleaning;
    this.nextCleaning = nextCleaning;
    this.updatedAt = new Date();
  }
}